import { keys } from '@/gql/global/keys';
import { APIError } from '@/gql/global/types';
import { GET_USER_INFO, UPDATE_USER_BY_PK } from '@/gql/user';
import { GetUserInfoResponse, UpdateUserByPkPayload, UpdateUserByPkResponse } from '@/gql/user/types';
// OAuth functions
import { getCurrentIdToken, handleOAuthCallback, initiateOAuthLogin } from '@/services/auth/oauthService';
import { clearTokens, getRefreshToken } from '@/utils/oauthPKCE';
import { fetchData } from '@/services/graphql';
import { getCookie, setCookie } from '@/utils/cookies';
import { encryptData } from '@/utils/encryption';
import { invalidateQueries } from '@/utils/tanStackQuery';
import { UseMutationOptions, useMutation, useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';

// Updated User type to match OAuth response
export type User = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  programTag?: string;
  tonyAiAccess?: string;
};

export const useUpdateUserInfo = (
  options?: Omit<UseMutationOptions<UpdateUserByPkResponse, APIError, UpdateUserByPkPayload>, 'onSuccess'>,
) => {
  return useMutation({
    mutationFn: (payload: UpdateUserByPkPayload) => fetchData<UpdateUserByPkResponse>(UPDATE_USER_BY_PK, payload),
    ...options,

    onSuccess: () => {
      invalidateQueries([keys.user.info.queryKey]);
    },
  });
};

export const useUserInfo = () => {
  const location = useLocation();
  // Don't fetch if we're processing OAuth callback (no tokens yet)
  const isOAuthCallback = location.search.includes('code=');
  // Don't fetch if there's no sign of a session (e.g., public pages visited by
  // unauthenticated users). The in-memory id token is always empty right after a page
  // load, so also accept a stored refresh token — a logged-in user always has one, an
  // anonymous visitor never does. Gating on the id token alone forced every page load
  // to wait for the token refresh before this query (and everything gated behind it)
  // could start.
  const hasAuthToken = !!getCurrentIdToken() || !!getRefreshToken();

  return useQuery({
    queryKey: keys.user.info.queryKey,
    queryFn: () => fetchData<GetUserInfoResponse>(GET_USER_INFO),
    onSuccess: (data) => {
      // Extract user data for iframe
      const userData = {
        email: data.me.email,
        firstName: data.me.firstName,
        lastName: data.me.lastName,
        id: data.me.id,
      };

      // Encrypt and store in cookie
      const encryptedData = encryptData(userData);
      setCookie('iframe-auth', encryptedData, {
        expires: 86400,
        path: '/',
        secure: true,
        sameSite: 'None',
        domain: '.rpmplanner.com',
      });
    },
    enabled: !isOAuthCallback && hasAuthToken,
    // Refetch every 30 seconds if cookie doesn't exist
    refetchInterval: (data) => {
      if (!data) return false;
      const authCookie = getCookie('iframe-auth');
      return authCookie ? false : 30000;
    },
  });
};

// OAuth callback handler hook.
// On failure: clear stale local tokens and restart login with clear_sso so the
// user sees the login form instead of being silently re-authenticated into
// another failing callback. initiateOAuthLogin generates and stores a fresh
// PKCE pair — without it the next code comes back unbound to any challenge and
// the token exchange can never succeed, looping forever.
export const useOAuthCallback = () => {
  return useCallback(async (code: string) => {
    try {
      await handleOAuthCallback(code);
    } catch (error) {
      console.error('OAuth callback failed:', error);
      clearTokens();
      await initiateOAuthLogin({ clearSso: true });
    }
  }, []);
};
